// Notification categories — the single source of truth for which notification
// types live under which tab of the bell panel and the /notifications page.
//
// Deliberately imports nothing. The root-level vitest suite (and root
// `tsc --noEmit`, which has no DOM lib and no `import.meta.env`) imports this
// file directly from `tests/`, the same way it imports `format.ts`. Anything
// that pulls in `queries.ts` / `supabase.ts` would break that typecheck.

// Every value the DB CHECK constraint allows (supabase/migrations/059_
// leave_reminders.sql). `new_ticket_assigned` is allowed by the DB but no
// producer writes it today; it is listed so the Assigned tab covers it if one
// ever does.
export const NOTIFICATION_TYPES = [
  'client_replied',
  'sla_breach',
  'follow_up_sent',
  'follow_up_reminder',
  'reassigned_to_you',
  'new_ticket_assigned',
  'ticket_escalated',
  'ticket_resolved',
  'note_added',
  'note_mention',
  'added_as_watcher',
  'leave_requested',
  'leave_decided',
  'leave_approved_team',
  'leave_reminder',
] as const;

export type NotificationType = (typeof NOTIFICATION_TYPES)[number];

export type NotificationCategoryId =
  | 'replies'
  | 'assigned'
  | 'notes'
  | 'resolved'
  | 'alerts'
  | 'team';

export type NotificationTabId = 'all' | NotificationCategoryId;

export interface NotificationTab {
  id: NotificationTabId;
  label: string;
  /** `null` = no type filter (the All tab). */
  types: readonly NotificationType[] | null;
}

// Exhaustive on purpose: adding a value to NOTIFICATION_TYPES without deciding
// its tab here is a compile error, not a notification that silently only
// shows up under All.
const CATEGORY_BY_TYPE: Record<NotificationType, NotificationCategoryId> = {
  client_replied: 'replies',
  reassigned_to_you: 'assigned',
  new_ticket_assigned: 'assigned',
  added_as_watcher: 'assigned',
  note_added: 'notes',
  note_mention: 'notes',
  ticket_resolved: 'resolved',
  sla_breach: 'alerts',
  ticket_escalated: 'alerts',
  follow_up_sent: 'alerts',
  // Your own reminder to follow up (migration 082).
  follow_up_reminder: 'alerts',
  leave_requested: 'team',
  leave_decided: 'team',
  leave_approved_team: 'team',
  leave_reminder: 'team',
};

const CATEGORY_LABELS: Record<NotificationCategoryId, string> = {
  replies: 'Replies',
  assigned: 'Assigned',
  notes: 'Notes',
  resolved: 'Resolved',
  alerts: 'Alerts',
  team: 'Team',
};

// Tab order in the UI.
const CATEGORY_ORDER: readonly NotificationCategoryId[] = [
  'replies',
  'assigned',
  'notes',
  'resolved',
  'alerts',
  'team',
];

export const NOTIFICATION_TABS: readonly NotificationTab[] = [
  { id: 'all', label: 'All', types: null },
  ...CATEGORY_ORDER.map((id) => ({
    id,
    label: CATEGORY_LABELS[id],
    types: NOTIFICATION_TYPES.filter((t) => CATEGORY_BY_TYPE[t] === id),
  })),
];

const CATEGORY_LOOKUP: ReadonlyMap<string, NotificationCategoryId> = new Map(
  Object.entries(CATEGORY_BY_TYPE),
);

/** Owning category for a type, or `null` for a type nobody has mapped yet. */
export function categoryOf(type: string): NotificationCategoryId | null {
  return CATEGORY_LOOKUP.get(type) ?? null;
}

/** Types a tab filters on, or `null` for All (no filter). */
export function typesForTab(tab: NotificationTabId): readonly NotificationType[] | null {
  return NOTIFICATION_TABS.find((t) => t.id === tab)?.types ?? null;
}

/** Validate a raw URL param into a tab id; anything unrecognised means All. */
export function parseNotificationTab(raw: string | null | undefined): NotificationTabId {
  return NOTIFICATION_TABS.find((t) => t.id === raw)?.id ?? 'all';
}

/** Query string for a view — empty for the default (All, not unread-only). */
export function notificationsSearch(tab: NotificationTabId, unreadOnly: boolean): string {
  const parts: string[] = [];
  if (tab !== 'all') parts.push(`tab=${tab}`);
  if (unreadOnly) parts.push('unread=1');
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

// Empty-state wording for a category with nothing in it. Written per tab
// because "No replies notifications yet" reads badly when generated.
const EMPTY_TITLE: Record<NotificationTabId, string> = {
  all: 'No notifications yet',
  replies: 'No client replies yet',
  assigned: 'Nothing assigned yet',
  notes: 'No notes or mentions yet',
  resolved: 'Nothing resolved yet',
  alerts: 'No alerts',
  team: 'No team notices yet',
};

/** Title (and optional subtitle) for an empty view. */
export function notificationEmptyCopy(
  tab: NotificationTabId,
  unreadOnly: boolean,
): { title: string; subtitle?: string } {
  if (unreadOnly) {
    if (tab === 'all') {
      return { title: 'No unread notifications', subtitle: "You're all caught up." };
    }
    const label = NOTIFICATION_TABS.find((t) => t.id === tab)?.label ?? tab;
    return { title: `Nothing unread in ${label}` };
  }
  if (tab === 'all') {
    return {
      title: EMPTY_TITLE.all,
      subtitle: 'Once something happens on a ticket assigned to you, it will land here.',
    };
  }
  return { title: EMPTY_TITLE[tab] };
}

/** Whether a row belongs in a given tab / unread-only view. */
export function matchesView(
  n: { type: string; read_at: string | null },
  tab: NotificationTabId,
  unreadOnly: boolean,
): boolean {
  if (unreadOnly && n.read_at !== null) return false;
  const types = typesForTab(tab);
  return types === null || (types as readonly string[]).includes(n.type);
}

// -------------------- Unread counts --------------------

export type UnreadTypeCounts = Record<string, number>;

export function countByType(rows: ReadonlyArray<{ type: string }>): UnreadTypeCounts {
  const counts: UnreadTypeCounts = {};
  for (const r of rows) counts[r.type] = (counts[r.type] ?? 0) + 1;
  return counts;
}

/**
 * Per-tab unread badges. `totalUnread` (the exact head count) drives All so
 * unmapped types still register; category tabs sum their own types.
 */
export function unreadByTab(
  counts: UnreadTypeCounts | undefined,
  totalUnread: number | undefined,
): Record<NotificationTabId, number> {
  const summed = counts ? Object.values(counts).reduce((a, b) => a + b, 0) : 0;
  const result = {} as Record<NotificationTabId, number>;
  for (const tab of NOTIFICATION_TABS) {
    result[tab.id] =
      tab.types === null
        ? (totalUnread ?? summed)
        : counts
          ? tab.types.reduce((n, t) => n + (counts[t] ?? 0), 0)
          : 0;
  }
  return result;
}

/** Adjust one type's unread count by `delta`, clamped at zero. Pure. */
export function bumpTypeCount(
  counts: UnreadTypeCounts,
  type: string,
  delta: number,
): UnreadTypeCounts {
  return { ...counts, [type]: Math.max(0, (counts[type] ?? 0) + delta) };
}

// -------------------- Optimistic cache patches --------------------
//
// Both helpers return the SAME array reference when nothing changed, so React
// Query's structural sharing keeps untouched caches from re-rendering.

/**
 * Set one row's `read_at`. Marking read never overwrites an existing read
 * timestamp (the server write is guarded by `.is('read_at', null)`); marking
 * unread (`null`) always applies.
 */
export function applyReadState<T extends { id: string; read_at: string | null }>(
  rows: T[],
  id: string,
  readAt: string | null,
): T[] {
  let changed = false;
  const next = rows.map((r) => {
    if (r.id !== id || r.read_at === readAt) return r;
    if (readAt !== null && r.read_at !== null) return r;
    changed = true;
    return { ...r, read_at: readAt };
  });
  return changed ? next : rows;
}

/** Mark every unread row that satisfies `match` as read at `readAt`. */
export function applyReadStateWhere<T extends { type: string; read_at: string | null }>(
  rows: T[],
  match: (n: T) => boolean,
  readAt: string,
): T[] {
  let changed = false;
  const next = rows.map((r) => {
    if (r.read_at !== null || !match(r)) return r;
    changed = true;
    return { ...r, read_at: readAt };
  });
  return changed ? next : rows;
}
